import logger from "./logger"
import settings from "./settings"
import visit from "@/core/visit"

export class FetchError extends Error {
  readonly reporter = "client:fetch"

  readonly url: string
  readonly options: RequestInit | undefined
  readonly response: Response | undefined

  constructor(url: string, options: RequestInit | undefined, response: Response | undefined) {
    super(response ? `Request failed with status code ${response.status}` : "Request failed without response")
    this.url = url
    this.options = options
    this.response = response
  }
}

/**
 * True when a FetchError never got a response back at all (network failure, CORS,
 * ad-blocker, or client-side timeout). These never reach the server, so they're
 * unactionable client-side noise rather than a signal of a real backend problem.
 */
export function isUnreachable(e: unknown): boolean {
  return e instanceof FetchError && !e.response
}

export async function fetch(url: string, options?: RequestInit): Promise<Response> {
  try {
    const response = await execute(url, options)
    if (!response.ok) {
      throw new FetchError(url, options, response)
    }
    return response
  } catch (err) {
    if (err instanceof FetchError) {
      throw err
    }
    if (!settings.jsErrorUrl || !url.startsWith(settings.jsErrorUrl)) {
      // skip logging fetch errors to jsErrorUrl to avoid infinite loop
      logger.warn(`Fetch request to ${url} failed:`, err)
    }
    throw new FetchError(url, options, undefined)
  }
}

let pendingRequest: Promise<Response> | null = null

async function execute(url: string, options: RequestInit = {}): Promise<Response> {
  if (serializeCheck(url, options?.method)) {
    if (pendingRequest) {
      await pendingRequest.catch(() => {})
    }
    const request = executeRequest(url, options)
    pendingRequest = request
    try {
      return await request
    } finally {
      pendingRequest = null
    }
  }
  return executeRequest(url, options)
}

function executeRequest(url: string, options: RequestInit = {}): Promise<Response> {
  // add opt-out header if needed
  if (visit.isDoNotTrack()) {
    options.headers = {
      ...(options.headers ?? {}),
      "X-Nosto-Optout": "1"
    }
  }

  // automatically add customer id to ev1 requests, if missing
  let finalUrl = url
  if (url.startsWith(`${settings.server}/ev1`)) {
    const newUrl = new URL(url)
    const customerId = visit.getCustomerId()
    if (!newUrl.searchParams.has("c") && customerId) {
      newUrl.searchParams.append("c", customerId)
      finalUrl = newUrl.toString()
    }
  }

  // Wrap fetch with 10-second timeout
  return Promise.race([window.fetch(finalUrl, options), timeout(10000)])
}

function timeout(ms: number) {
  return new Promise<Response>((_resolve, reject) => {
    setTimeout(() => reject(new Error(`Request timeout after ${ms} ms`)), ms)
  })
}

function serializeCheck(url: string, method = "GET") {
  const nostoRequest = url.startsWith(settings.server)
  if (nostoRequest) {
    return method === "POST" || url.includes("/ev1") || url.includes("/cmp-mapping/")
  }
  return false
}
